import { Injectable } from '@angular/core';
import { Shipment } from '@shared-features/models/shipment';
import { ShipmentCreationTemplateTypes } from '@shared/enums/shipment-creation-template-types.enum';
import { enumToArray } from '@shared/utils/enum-to-array.util';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { ShipmentsListType } from '@shared/enums/shipments-list-type.enum';

@Injectable({
  providedIn: 'root',
})
export class ShipmentTemplateService {
  private RoutesPerTemplate = {
    [ShipmentCreationTemplateTypes.Basic]: {
      add: RoutesUtil.ShipmentTemplateBasicAdd,
      edit: RoutesUtil.ShipmentTemplateBasicEdit,
    },

    // A bidding shipment is created and edited through the basic template's pages.
    [ShipmentCreationTemplateTypes.Bidding]: {
      add: RoutesUtil.ShipmentTemplateBasicAdd,
      edit: RoutesUtil.ShipmentTemplateBasicEdit,
    },

    [ShipmentCreationTemplateTypes.Cement]: {
      add: RoutesUtil.ShipmentTemplateCementAdd,
      edit: RoutesUtil.ShipmentTemplateCementEdit,
    },
    [ShipmentCreationTemplateTypes.Orders]: {
      add: RoutesUtil.ShipmentTemplateOrdersAdd,
      edit: null,
    },

    // A creation method, so it has an `add` and nothing to edit: a shipment created from a file
    // is an ordinary basic shipment afterwards and is edited as one.
    [ShipmentCreationTemplateTypes.Import]: {
      add: RoutesUtil.ShipmentTemplateBulkImport,
      edit: null,
    },
  };

  private templates = enumToArray(ShipmentCreationTemplateTypes);
  /**
   * A template counts as available only when it actually has routes here — checking the enum
   * instead let a mapped-nowhere template through and produced an `undefined` URL, which the
   * router resolved to the wildcard route.
   */
  private isTemplateAvailable(template: ShipmentCreationTemplateTypes): boolean {
    if (!template) {
      return false;
    }

    return (
      !!this.templates.find((t) => t.value === template) && !!this.RoutesPerTemplate[template]
    );
  }

  shipmentsListType: ShipmentsListType;

  constructor() {}

  getEditUrl(shipment: Shipment, shipmentsListType: ShipmentsListType): string {
    const { shipmentCreationTemplate, customer } = shipment;
    const customerDefaultTemplate = ((customer as any)?.templates || []).find((t) => t.isDefault);

    const editUrl = (template: ShipmentCreationTemplateTypes): string =>
      this.RoutesPerTemplate[template]?.edit?.url({ id: shipment.id, type: shipmentsListType });

    // Never hand the router an undefined URL — a template with no edit page of its own
    // (Orders) falls back to the basic one rather than bouncing to the wildcard route.
    return (
      (this.isTemplateAvailable(shipmentCreationTemplate) && editUrl(shipmentCreationTemplate)) ||
      (this.isTemplateAvailable(customerDefaultTemplate?.type) &&
        editUrl(customerDefaultTemplate.type)) ||
      editUrl(ShipmentCreationTemplateTypes.Basic)
    );
  }

  getCreateUrl(
    template?: (typeof ShipmentCreationTemplateTypes)[keyof typeof ShipmentCreationTemplateTypes],
    redirect?: string
  ): string {
    if (!template || !this.isTemplateAvailable(template)) {
      return this.RoutesPerTemplate[ShipmentCreationTemplateTypes.Basic].add.url(undefined, {
        redirect,
      });
    }
    return this.RoutesPerTemplate[template].add.url(undefined, { redirect });
  }
}
